import * as Sentry from '@sentry/react';
class MemoryCleanup {
  constructor() {
    this.cleanupInterval = null;
    this.memoryCheckInterval = null;
    this.isActive = false;
  }

  forceGC() {
    if (typeof window !== 'undefined' && window.gc) {
      try {
        window.gc();
        console.log('[MemoryCleanup] Forced garbage collection');
      } catch (error) {
        console.warn('[MemoryCleanup] GC not available');
      }
    }
  }

  performCleanup() {
    try {
      const now = Date.now();
      const cutoff = now - 300000;
      const cartTimestamp = localStorage.getItem('cart_last_update');
      if (cartTimestamp && parseInt(cartTimestamp) < cutoff) {
        localStorage.removeItem('cart_last_update');
      }
    } catch (error) {
      console.warn('[MemoryCleanup] Cleanup error:', error);
    }
  }

  startPeriodicCleanup() {
    if (this.isActive) return;
    
    this.isActive = true;
    this.cleanupInterval = setInterval(() => {
      this.performCleanup();
    }, 600000);

    console.log('[MemoryCleanup] Periodic cleanup started');
  }

  stopPeriodicCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.isActive = false;
    
    console.log('[MemoryCleanup] Periodic cleanup stopped');
  }

  startMemoryPressureMonitoring() {
    if (this.memoryCheckInterval || !('memory' in performance)) return;

    this.memoryCheckInterval = setInterval(() => {
      const memInfo = performance.memory;
      const usedPercent = (memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100;
      if (usedPercent > 80) {
        memoryCleanup.emergencyCleanup();
      }
    }, 600000);
  }

  stopMemoryPressureMonitoring() {
    if (this.memoryCheckInterval) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = null;
    }
  }

  emergencyCleanup() {
    if (!this.emergencyReported) {
      this.emergencyReported = true;
      Sentry.captureMessage('[MemoryCleanup] Device entered emergency cleanup', 'warning');
    }
    Sentry.addBreadcrumb({
      category: 'memory',
      message: '[MemoryCleanup] Emergency cleanup triggered',
      level: 'warning',
      data: performance.memory
        ? {
            usedMB: Math.round(performance.memory.usedJSHeapSize / 1048576),
            limitMB: Math.round(performance.memory.jsHeapSizeLimit / 1048576),
          }
        : undefined,
    });
    this.performCleanup();

    import('./memoryMonitor')
      .then(({ memoryMonitor }) => {
        if (memoryMonitor && typeof memoryMonitor.performEmergencyCleanup === 'function') {
          memoryMonitor.performEmergencyCleanup();
        }
      })
      .catch(() => {});
  }
}

export const memoryCleanup = new MemoryCleanup();

if (typeof window !== 'undefined') {
  const isLowMemoryDevice =
    window.innerWidth < 768 || (navigator.deviceMemory && navigator.deviceMemory <= 2);

  const syncMemoryCleanupState = () => {
    const isVisible = document.visibilityState === 'visible';

    if (isLowMemoryDevice && isVisible) {
      memoryCleanup.startPeriodicCleanup();
      memoryCleanup.startMemoryPressureMonitoring();
    } else {
      memoryCleanup.stopPeriodicCleanup();
      memoryCleanup.stopMemoryPressureMonitoring();
    }
  };

  setTimeout(syncMemoryCleanupState, 10000);
  document.addEventListener('visibilitychange', syncMemoryCleanupState);

  window.addEventListener('beforeunload', () => {
    memoryCleanup.stopPeriodicCleanup();
    memoryCleanup.stopMemoryPressureMonitoring();
  });

  window.memoryCleanup = memoryCleanup;
}

export default memoryCleanup;
